"use client";

import {JSX, memo} from "react";

import {useSnapshot} from "valtio";

import {useTranslation} from "@/shared/i18n";
import {type MonthData, type ScrollableDateRangePickerInstance} from "@/shared/models/form/datepickers";
import {ButtonBase} from "@/shared/ui/buttons";
import {Typography} from "@/shared/ui/data-display/typography";

import {WEEKDAY_KEYS} from "../constants";
import {CalendarMonthView} from "./calendar-month-view";

import "../range/daterange-picker-field.scss";
import "./styles/scrollable-calendar.scss";

export interface ScrollableCalendarProps {
    model: ScrollableDateRangePickerInstance;
    className?: string;
}

export const ScrollableCalendar = memo(function ScrollableCalendar({model, className}: ScrollableCalendarProps): JSX.Element {
    const snapshot = useSnapshot(model);
    const {t} = useTranslation("calendar");
    const {t: tSearch} = useTranslation("search");

    const months = snapshot.scrollableMonthsCompact as Array<MonthData>;

    return (
        <div className={`scrollable-calendar ${className || ""}`} onMouseLeave={model.clearHoverDate}>
            {/* Sticky weekdays header */}
            <div className="scrollable-calendar__weekdays">
                {WEEKDAY_KEYS.map((key) => (
                    <Typography
                        key={key}
                        as="div"
                        size={12}
                        weight={600}
                        color="paragraph"
                        options={["uppercase"]}
                        className="scrollable-calendar__weekday"
                    >
                        {t(key as Parameters<typeof t>[0])}
                    </Typography>
                ))}
            </div>

            {/* Scrollable months container */}
            <div className="scrollable-calendar__months">
                {months.map((monthData) => (
                    <CalendarMonthView
                        key={`${monthData.year}-${monthData.month}`}
                        monthData={monthData}
                        onDayClick={model.selectDay}
                        onDayHover={model.handleDayHover}
                    />
                ))}

                {/* Load more button */}
                {snapshot.canLoadMore && (
                    <ButtonBase variant="secondary" onClick={() => model.loadMoreMonths()} className="scrollable-calendar__load-more">
                        {tSearch("loadMoreDates")}
                    </ButtonBase>
                )}
            </div>
        </div>
    );
});
